import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  DataGrid,
  GridToolbar,
  GridColDef,
  GridRenderCellParams,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { TreeData } from "src/modules/patient/patient-care-add/data";
import { GridToolbarContainer } from "@mui/x-data-grid";
import { useTranslate, useLocales } from "src/locales";
import ByzMuiOpenProcessMenu from "../../ByzMuiOpenProcessMenu";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { getGridLocaleText } from "../gridLocaleText";
import { normalizeString } from "src/utils/text";
// MPOGLU;443876;19022024
interface ByzDataTreeListCarePlanProps {
  initialData: any;
  columns: GridColDef[];
  onEditClick?: (row: any) => void;
  onRowClick?: (row: any) => void;
  customToolbar?: any;
  hideColumns?: boolean;
  onSelectionChange?: (ids: number[]) => void;
  showFavoriteInMenu?: boolean;
  onFavoriteToggle?: (id: number, isFavorite: boolean) => void;
  favoriteIds?: number[];
  showDefaultMenuItems?: boolean;
  onDeleteGuide?: (guideId: number) => void;
  onDeletePlan?: (planId: number) => void;
  onCopyGuide?: (guideId: number) => void;
  onPasteGuide?: () => void;
  canPasteGuide?: boolean;
  sx?: any;
  treeColumn?: string;
  whiteGrid?: boolean;
  actionColumnPosition?: "start" | "end"; // default: start
  showFooter?: boolean;
  checkboxSelection?: boolean;
  isRowSelectable?: (params: any) => boolean;
  getRowClassName?: (params: any) => string;
  isActionColumnShow?: boolean;
  selectedIds?: number[];
  defaultExpandAll?: boolean;
  autoHeight?: boolean;
  showToolbar?: boolean;
}

function CustomToolbarDef({ searchInput, onSearchInputChange, onSearch }: {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearch: () => void;
}) {
  const { t } = useTranslate();
  const [localInput, setLocalInput] = useState(searchInput);

  useEffect(() => {
    setLocalInput(searchInput);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchInputChange(localInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [localInput, onSearchInputChange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalInput(event.target.value);
  };

  return (
    <GridToolbarContainer sx={{ color: "#00B8D9" }}>
      <GridToolbar />
      <GridToolbarQuickFilter
        placeholder={t("search")}
        sx={{ ml: "auto", minWidth: 300 }}
        value={localInput}
        onChange={handleChange}
      />
    </GridToolbarContainer>
  );
}

const ByzDataTreeListCarePlan: React.FC<ByzDataTreeListCarePlanProps> = ({
  initialData,
  columns,
  customToolbar,
  hideColumns,
  onSelectionChange,
  showFavoriteInMenu = false,
  onFavoriteToggle,
  favoriteIds = [],
  showDefaultMenuItems = true,
  onDeleteGuide,
  onDeletePlan,
  onCopyGuide,
  onPasteGuide,
  canPasteGuide = false,
  sx,
  treeColumn,
  whiteGrid = false,
  actionColumnPosition = "start",
  showFooter = true,
  checkboxSelection = false,
  isRowSelectable,
  getRowClassName,
  isActionColumnShow = true,
  selectedIds: propSelectedIds,
  defaultExpandAll = false,
  autoHeight = false,
  showToolbar = true,
}) => {
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // const [copiedIds, setCopiedIds] = useState<number[]>([]); // Unused state removed
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    if (propSelectedIds !== undefined) {
      setSelectedIds(propSelectedIds);
    }
  }, [propSelectedIds]);

  useEffect(() => {
    if (searchTerm && searchTerm.trim() !== '') {
      const searchNormalized = normalizeString(searchTerm.trim());
      const matchingRows = initialData.filter((row: TreeData) =>
        normalizeString(row.name).includes(searchNormalized)
      );

      const parentsToExpand = new Set<number>();
      matchingRows.forEach((row: TreeData) => {
        if (row.parentId !== null) {
          parentsToExpand.add(row.parentId);
        }
      });
      setExpandedRows(prev => {
        const newExpanded = { ...prev };
        parentsToExpand.forEach(parentId => {
          newExpanded[parentId] = true;
        });
        return newExpanded;
      });
    }
  }, [searchTerm, initialData]);

  useEffect(() => {
    if (defaultExpandAll && initialData && initialData.length > 0) {
      const allParents: Record<number, boolean> = {};
      initialData.forEach((row: any) => {
        if (initialData.some((child: any) => child.parentId === row.id)) {
          allParents[row.id] = true;
        }
      });
      setExpandedRows(prev => ({ ...prev, ...allParents }));
    }
  }, [initialData, defaultExpandAll]);

  const hasChildren = (id: number): boolean => {
    return initialData.some((row: any) => row.parentId === id);
  };

  const toggleRow = (id: number): void => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const enhancedColumns: GridColDef[] = columns.map((col) => {
    if (col.field === treeColumn) {
      return {
        ...col,
        renderCell: (params: GridRenderCellParams<TreeData>) => {
          const isChild = params.row.parentId !== null;
          const showExpandIcon = hasChildren(params.row.id);

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                position: "relative",
                paddingLeft: isChild ? 20 : 0,
              }}
            >
              {isChild && (
                <div
                  style={{
                    position: "absolute",
                    left: 6,
                    top: "50%",
                    marginTop: -6,
                    width: 12,
                    height: 12,
                    borderLeft: "1.5px solid #C4C4C4",
                    borderBottom: "1.5px solid #C4C4C4",
                    borderBottomLeftRadius: 6,
                    pointerEvents: "none",
                  }}
                />
              )}

              {showExpandIcon && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRow(params.row.id);
                  }}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    marginRight: 4,
                  }}
                >
                  {expandedRows[params.row.id] ? (
                    <KeyboardArrowDown fontSize="small" />
                  ) : (
                    <KeyboardArrowRight fontSize="small" />
                  )}
                </div>
              )}

              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {params.value}
              </span>
            </div>
          );
        },
      };
    }

    return col;
  });

  const getVisibleRows = useCallback((searchTerm?: string): TreeData[] => {
    if (!searchTerm || searchTerm.trim() === '') {
      return initialData.filter((row: TreeData) => {
        if (row.parentId === null) return true;
        return expandedRows[row.parentId];
      });
    }

    const searchNormalized = normalizeString(searchTerm.trim());

    const matchingRows = initialData.filter((row: TreeData) =>
      normalizeString(row.name).includes(searchNormalized)
    );

    const rowsToShow = new Set<number>();

    matchingRows.forEach((row: TreeData) => {
      rowsToShow.add(row.id);

      if (row.parentId !== null) {
        rowsToShow.add(row.parentId);
      }

      if (row.parentId === null) {
        const children = initialData.filter((child: TreeData) => child.parentId === row.id);
        children.forEach((child: TreeData) => rowsToShow.add(child.id));
      }
    });

    const result: TreeData[] = [];

    const addParentAndChildren = (parentId: number | null) => {
      if (parentId !== null && !expandedRows[parentId]) {
        return;
      }

      const children = initialData.filter((row: TreeData) =>
        row.parentId === parentId && rowsToShow.has(row.id)
      );

      children.forEach((child: TreeData) => {
        result.push(child);
        addParentAndChildren(child.id);
      });
    };

    addParentAndChildren(null);

    return result;
  }, [initialData, expandedRows]);

  const normalVisibleRows = useMemo(() => {
    return initialData.filter((row: TreeData) => {
      if (row.parentId === null) return true;
      return expandedRows[row.parentId];
    });
  }, [initialData, expandedRows]);

  const searchVisibleRows = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return normalVisibleRows;
    }
    return getVisibleRows(searchTerm);
  }, [searchTerm, getVisibleRows, normalVisibleRows]);

  const visibleRows = searchVisibleRows;

  // let selectedId = selectedIds; // Unused variable removed
  const handleProcessAction = useCallback(async (clickedId?: number) => {
    try {
      // Sadece tek satır seçimi yapılabilir (MUI DataGrid ücretsiz versiyonu için)
      let selectedId = clickedId || (selectedIds.length > 0 ? selectedIds[0] : null);

      if (!selectedId) {
        // Eğer hiç seçim yoksa, ilk child satırı seç
        const firstChild = initialData.find((item: TreeData) => item.parentId !== null);
        selectedId = firstChild ? firstChild.id : null;
      }

      if (!selectedId) {
        return [];
      }

      const row = initialData.find((item: TreeData) => item.id === Number(selectedId));

      if (!row) {
        return [];
      }

      const idsToProcess: number[] = [];

      // Eğer parent satır seçildiyse, tüm child'ları da dahil et
      if (row.parentId === null) {
        idsToProcess.push(Number(selectedId));
        const relatedRecords = initialData.filter(
          (item: TreeData) => item.parentId === Number(selectedId)
        );
        idsToProcess.push(...relatedRecords.map((item: TreeData) => item.id));
      } else {
        // Child satır seçildiyse, sadece o satırı işle
        idsToProcess.push(Number(selectedId));
      }

      const uniqueIdsToProcess = Array.from(new Set(idsToProcess));

      // Sadece tek satır seçimi yap (MUI DataGrid ücretsiz versiyonu için)
      setSelectedIds([selectedId]);

      return uniqueIdsToProcess;
    } catch (error) {
      console.error("İşlem sırasında hata oluştu:", error);
      return [];
    }
  }, [selectedIds, initialData]);

  // const fetchCareDataNurse = async (pcsid: number) => {
  //   try {
  //     if (pcsid) {
  //       const data = await PatientCareService.delete({}, pcsid); // pcsid gönder
  //       setCareData(data);  // Yeni verileri careData state'ine kaydet
  //     }
  //   } catch (error) {
  //     console.error('Nurse bakım listesi yüklenirken hata:', error);
  //   }
  // };

  // const fetchCareDataNurse = async (pcsid: number) => {
  //   try {
  //     if (pcsid) {
  //       const data = await PatientCareService.add({}, pcsid); // pcsid gönder
  //       setCareData(data);  // Yeni verileri careData state'ine kaydet
  //     }
  //   } catch (error) {
  //     console.error('Nurse bakım listesi yüklenirken hata:', error);
  //   }
  // };
  const { t } = useTranslate();
  const { currentLang } = useLocales();
  const handleProcessActionDelete = useCallback(async (
    action: string,
    uniqueIdsToProcess: number[]
  ) => {
    try {

      console.log(
        `listOpenProcessMenuSelected API cevabı: ${action} işlemi uygulanacak ID'ler:`,
        uniqueIdsToProcess
      );
    } catch (error) {
      console.error("İşlem sırasında hata oluştu:", error);
    }
  }, []);

  const handleDeleteClick = useCallback(async () => {
    const uniqueIdsToProcess = await handleProcessAction();
    if (uniqueIdsToProcess) {
      await handleProcessActionDelete("Sil", uniqueIdsToProcess);
    } else {
      console.error("Silinecek ID'ler bulunamadı.");
    }
  }, [handleProcessAction, handleProcessActionDelete]);

  /* 
  const handleProcessActionCopy = async (
    action: string,
    uniqueIdsToProcess: number[]
  ) => {
    try {
      setCopiedIds(uniqueIdsToProcess);
      console.log(
        `listOpenProcessMenuSelected API cevabı: ${action} işlemi uygulanacak ID'ler:`,
        uniqueIdsToProcess
      );
    } catch (error) {
      console.error("İşlem sırasında hata oluştu:", error);
    }
  };
  
  const handleProcessActionPaste = async (
    action: string
  ) => {
    try {
      console.log(
        `listOpenProcessMenuSelected API cevabı: ${action} işlemi uygulanacak ID'ler:`,
        copiedIds
      );
  
    } catch (error) {
      console.error("İşlem sırasında hata oluştu:", error);
    }
  };
  */
  /*
    const handleCopyClick = async () => {
      const uniqueIdsToProcess = await handleProcessAction();
      if (uniqueIdsToProcess) {
        await handleProcessActionCopy("Kopyala", uniqueIdsToProcess);
      } else {
        console.error("Kopyalancak ID'ler bulunamadı.");
      }
    };
   
    const handlePasteClick = async () => {
      if (copiedIds.length > 0) {
        await handleProcessActionPaste("Yapıştır");
      } else {
        console.error("Yapıştırılacak ID'ler bulunamadı.");
      }
    };
    */


  const actionColumn: GridColDef = useMemo(() => ({
    field: "actions",
    headerName: "",
    width: 60,
    renderHeader: () => {
      const headerMenuItems = [
        ...(showDefaultMenuItems ? [
          {
            label: t("patient-care.delete-selected-guide"),
            onClick: () => {
              handleDeleteClick();
            },
          },
        ] : []),
        ...(onPasteGuide && canPasteGuide ? [
          {
            label: t("patient-care.paste-guide"),
            onClick: () => {
              onPasteGuide();
            },
          },
        ] : []),
      ];

      // Only show the header menu if there are items
      if (headerMenuItems.length === 0) {
        return null;
      }

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ByzMuiOpenProcessMenu
            menuItems={headerMenuItems}
            inSearch={true}
            onOpen={() => {
              handleProcessAction(0);
            }}
          />
        </div>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      // Determine if this is a guide (parent) or plan (child)
      const isGuide = params.row.parentId === null;
      const deleteLabel = isGuide ? t("patient-care.delete-selected-guide") : t("patient-care.delete-selected-plan");

      const handleDelete = () => {
        if (isGuide && onDeleteGuide) {
          onDeleteGuide(params.row.id);
        } else if (!isGuide && onDeletePlan) {
          onDeletePlan(params.row.id);
        }
      };

      const handleCopyGuide = () => {
        if (isGuide && onCopyGuide) {
          onCopyGuide(params.row.id);
        }
      };

      const handlePasteGuide = () => {
        if (isGuide && onPasteGuide) {
          onPasteGuide();
        }
      };

      const menuItems = [
        ...(showFavoriteInMenu && onFavoriteToggle ? [
          {
            label: t("favorites.add"),
            onClick: () => {
              const isFavorite = favoriteIds.includes(params.row.id);
              onFavoriteToggle(params.row.id, !isFavorite);
            },
            disabled: favoriteIds.includes(params.row.id),
          },
          {
            label: t("favorites.delete"),
            onClick: () => {
              const isFavorite = favoriteIds.includes(params.row.id);
              onFavoriteToggle(params.row.id, !isFavorite);
            },
            disabled: !favoriteIds.includes(params.row.id),
          },
        ] : []),
        ...(isGuide && onCopyGuide ? [
          {
            label: t("patient-care.copy-guide"),
            onClick: handleCopyGuide,
          },
        ] : []),
        ...(isGuide && onPasteGuide && canPasteGuide ? [
          {
            label: t("patient-care.paste-guide"),
            onClick: handlePasteGuide,
          },
        ] : []),
        ...(showDefaultMenuItems ? [
          {
            label: deleteLabel,
            onClick: handleDelete,
          },
        ] : []),
      ];

      // Eğer hiç menü öğesi yoksa action column'u gizle
      if (menuItems.length === 0) {
        return null;
      }

      return (
        <ByzMuiOpenProcessMenu
          menuItems={menuItems}
          hideHeaderTitle={true}
          inSearch={false}
          onOpen={() => {
            handleProcessAction(params.row.id);
          }}
        />
      );
    },
  }), [t, showDefaultMenuItems, onPasteGuide, canPasteGuide, showFavoriteInMenu, onFavoriteToggle, favoriteIds, onCopyGuide, onDeleteGuide, onDeletePlan, handleDeleteClick, handleProcessAction]);

  // Sadece bir child seçilebilsin ve parent (parentId === null) seçilemesin
  const handleRowSelectionModelChange = useCallback((newSelection: any) => {
    if (checkboxSelection) {
      // Checkbox selection varsa çoklu seçime izin ver
      const selectedIds = Array.isArray(newSelection) ? newSelection.map(Number) : [Number(newSelection)];
      setSelectedIds(selectedIds);
      if (onSelectionChange) onSelectionChange(selectedIds);
      return;
    }

    // Sadece bir seçim olsun
    let selectedId = Array.isArray(newSelection) ? newSelection[0] : newSelection;
    const selectedRow = visibleRows.find((row: any) => row.id === selectedId);
    if (!selectedRow || selectedRow.parentId === null) {
      setSelectedIds([]); // Parent veya boşsa seçim yok
      if (onSelectionChange) onSelectionChange([]);
      return;
    }
    setSelectedIds([selectedId]);
    if (onSelectionChange) onSelectionChange([selectedId]);
  }, [visibleRows, onSelectionChange, checkboxSelection]);

  const finalColumns = useMemo(() => {
    if (actionColumnPosition === "end") {
      return [...enhancedColumns, actionColumn];
    }
    if (!isActionColumnShow) {
      return [...enhancedColumns];
    }
    return [actionColumn, ...enhancedColumns];
  }, [actionColumnPosition, actionColumn, enhancedColumns, isActionColumnShow]);

  const paginationProps = showFooter
    ? {
      pagination: true as const,
      paginationModel,
      onPaginationModelChange: setPaginationModel,
      pageSizeOptions: [5, 10, 20],
    }
    : {};

  return (
    <DataGrid
      rows={visibleRows}
      autoHeight={autoHeight}
      columns={finalColumns}
      checkboxSelection={checkboxSelection}
      keepNonExistentRowsSelected
      disableRowSelectionOnClick={checkboxSelection}
      isRowSelectable={isRowSelectable ? isRowSelectable : (params) => params.row.parentId !== null}
      getRowClassName={getRowClassName}
      rowSelectionModel={selectedIds}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      disableColumnMenu
      localeText={getGridLocaleText(t, currentLang.value) as any}
      slots={{
        toolbar: !showToolbar ? null : (customToolbar === null ? (() => null) : (customToolbar || CustomToolbarDef)),
      }}
      slotProps={{
        toolbar: {
          showQuickFilter: false,
          searchInput: searchTerm,
          onSearchInputChange: setSearchTerm,
          onSearch: () => { },
        },
      }}
      sx={{
        ...(hideColumns && {
          "& .MuiDataGrid-columnHeaders": { display: "none" },
          "& .MuiDataGrid-virtualScroller": { marginTop: "0 !important" },
        }),
        ...(whiteGrid && {
          border: "none !important",
          "& .MuiDataGrid-root": {
            border: "none !important",
          },
          "& .MuiDataGrid-main": {
            border: "none !important",
          },
          "& .MuiDataGrid-row": {
            borderBottom: "1px solid #e5e5e5 !important",
            "&:last-of-type": {
              borderBottom: "none !important",
              "& .MuiDataGrid-cell": {
                borderBottom: "none !important",
              },
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #e5e5e5 !important",
            display: "flex",
            alignItems: "center",
            "&:last-of-type": {
              borderRight: "none !important",
            },
          },
          "& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
            borderBottom: "none !important",
          },
          ...(!hideColumns && {
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "1px solid #555 !important",
              backgroundColor: "white !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              minHeight: "unset !important",
              border: "none !important",
            },
          }),
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "white !important",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "#333 !important",
            fontWeight: "600 !important",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none !important",
            border: "none !important",
          },
          "& .MuiDataGrid-toolbarContainer": {
            "& .MuiButton-root": {
              color: "#309cf3",
              "&:hover": {
                backgroundColor: "rgba(48, 156, 243, 0.1)",
              },
            },
            "& .MuiTypography-root": {
              color: "#309cf3",
            },
            justifyContent: "space-between",
            alignItems: "center",
            "& > div:first-of-type": {
              display: "flex",
              gap: 1,
            },
          },
          "& .MuiDataGrid-footerContainer .MuiTablePagination-root": {
            margin: "0 !important",
            padding: "0 !important",
          },
        }),
        ...sx,
      }}
      {...paginationProps}
      hideFooter={!showFooter}
    />
  );
};

export default ByzDataTreeListCarePlan;
